import { useState, useEffect, useRef, useCallback } from 'react';

const SCENE_DURATION = 10000; // 10s per scene = 60s total

const scenes = [
  { id: 1, chapter: 'Introduction', title: 'Meet TreWaz AI', subtitle: 'Your 24/7 travel agency assistant' },
  { id: 2, chapter: 'Lead Capture', title: 'AI Captures Leads Instantly', subtitle: 'WhatsApp, web, email — all in one inbox' },
  { id: 3, chapter: 'CRM & Pipeline', title: 'Auto-Update Your CRM', subtitle: 'Every lead sorted, tracked and assigned' },
  { id: 4, chapter: 'Quote Builder', title: 'Quotes in Under 60 Seconds', subtitle: 'AI builds the package, you approve it' },
  { id: 5, chapter: 'Booking Flow', title: 'Confirm Bookings Faster', subtitle: 'From enquiry to invoice in one flow' },
  { id: 6, chapter: 'Autopilot', title: 'Your Agency on Autopilot', subtitle: 'Watch revenue grow while you sleep' },
];

/* ── Scene 1 – Intro stats ────────────────────────────── */
function Scene1() {
  const [count, setCount] = useState([0, 0, 0]);
  const targets = [500, 94, 3];
  useEffect(() => {
    const timer = setInterval(() => {
      setCount(prev => prev.map((v, i) => v < targets[i] ? Math.min(v + Math.ceil(targets[i] / 40), targets[i]) : v));
    }, 50);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 px-4">
      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500">
        <i className="ri-plane-line text-white text-3xl" />
      </div>
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900">TreWaz AI</h3>
        <p className="text-sm text-gray-500 mt-1">The AI brain powering modern travel agencies</p>
      </div>
      <div className="grid grid-cols-3 gap-3 w-full">
        {[
          { val: count[0], suffix: '+', label: 'Agencies', icon: 'ri-building-line', color: 'from-teal-50 to-teal-100 border-teal-200' },
          { val: count[1], suffix: '%', label: 'Lead Reply Rate', icon: 'ri-chat-check-line', color: 'from-emerald-50 to-emerald-100 border-emerald-200' },
          { val: count[2], suffix: '×', label: 'More Bookings', icon: 'ri-arrow-up-line', color: 'from-amber-50 to-amber-100 border-amber-200' },
        ].map((stat, i) => (
          <div key={i} className={`bg-gradient-to-b ${stat.color} border rounded-xl p-3 text-center`}>
            <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1">
              <i className={`${stat.icon} text-gray-600 text-base`} />
            </div>
            <p className="text-xl font-bold text-gray-900">{stat.val}{stat.suffix}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2">
        <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse inline-block" />
        <span className="text-xs text-teal-700 font-medium">Live AI running in 500+ agencies right now</span>
      </div>
    </div>
  );
}

/* ── Scene 2 – WhatsApp lead capture ──────────────────── */
function Scene2() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 600),
      setTimeout(() => setStep(2), 1800),
      setTimeout(() => setStep(3), 3200),
      setTimeout(() => setStep(4), 4800),
      setTimeout(() => setStep(5), 6000),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);
  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2">
      <div className="flex items-center gap-2 bg-[#075E54] rounded-t-lg px-3 py-2">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <i className="ri-user-line text-white text-sm" />
        </div>
        <div>
          <p className="text-white text-xs font-semibold">Sarah Ahmed</p>
          <p className="text-white/60 text-[10px]">online</p>
        </div>
        <i className="ri-whatsapp-line text-white/80 text-lg ml-auto" />
      </div>
      <div className="flex-1 bg-[#ECE5DD] rounded-lg p-2 flex flex-col gap-2 overflow-hidden">
        {step >= 1 && (
          <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[80%] self-start shadow-sm" style={{animation:'fadeSlideIn 0.4s ease'}}>
            <p className="text-xs text-gray-800">Hi! I want a Bali holiday for 2 adults, 7 nights in September 🌴</p>
            <p className="text-[10px] text-gray-400 text-right mt-1">10:23 AM</p>
          </div>
        )}
        {step >= 2 && (
          <div className="flex items-center gap-1 self-end">
            <div className="bg-[#DCF8C6] rounded-lg px-3 py-2 text-xs text-gray-800 shadow-sm">
              <span className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
              </span>
            </div>
          </div>
        )}
        {step >= 3 && (
          <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none px-3 py-2 max-w-[85%] self-end shadow-sm" style={{animation:'fadeSlideIn 0.4s ease'}}>
            <p className="text-[10px] text-teal-700 font-semibold mb-1">🤖 TreWaz AI</p>
            <p className="text-xs text-gray-800">Hi Sarah! Great choice — Bali is magical in September! 🌺 I have 3 amazing 7-night packages for 2 adults. What&apos;s your approximate budget?</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-[10px] text-gray-400">10:23 AM</p>
              <i className="ri-check-double-line text-blue-500 text-xs" />
            </div>
          </div>
        )}
        {step >= 4 && (
          <div className="bg-white rounded-lg rounded-tl-none px-3 py-2 max-w-[70%] self-start shadow-sm" style={{animation:'fadeSlideIn 0.4s ease'}}>
            <p className="text-xs text-gray-800">Around $2,500 for both of us</p>
            <p className="text-[10px] text-gray-400 text-right mt-1">10:24 AM</p>
          </div>
        )}
        {step >= 5 && (
          <div className="bg-[#DCF8C6] rounded-lg rounded-tr-none px-3 py-2 max-w-[85%] self-end shadow-sm" style={{animation:'fadeSlideIn 0.4s ease'}}>
            <p className="text-[10px] text-teal-700 font-semibold mb-1">🤖 TreWaz AI</p>
            <p className="text-xs text-gray-800">Perfect! I&apos;ve found 2 packages within budget. Sending details now + adding you to our VIP list ✅</p>
          </div>
        )}
      </div>
      {step >= 5 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex items-center gap-2" style={{animation:'fadeSlideIn 0.4s ease'}}>
          <i className="ri-user-add-fill text-emerald-600 text-sm" />
          <p className="text-xs text-emerald-700 font-medium">Lead captured → CRM • Quote queued • Follow-up set</p>
        </div>
      )}
    </div>
  );
}

/* ── Scene 3 – CRM Pipeline ───────────────────────────── */
function Scene3() {
  const [highlight, setHighlight] = useState(-1);
  useEffect(() => {
    let i = 0;
    const t = setInterval(() => { setHighlight(i); i = (i + 1) % 3; }, 1800);
    return () => clearInterval(t);
  }, []);
  const columns = [
    { title: 'New Leads', color: 'bg-blue-50 border-blue-200', dot: 'bg-blue-400', leads: [
      { name: 'Sarah Ahmed', pkg: 'Bali 7N • $2,500', time: 'just now', tag: 'AI captured' },
      { name: 'Omar Farooq', pkg: 'Dubai 5N • AED 6k', time: '5m ago', tag: 'WhatsApp' },
    ]},
    { title: 'Quoted', color: 'bg-amber-50 border-amber-200', dot: 'bg-amber-400', leads: [
      { name: 'Priya Nair', pkg: 'Maldives 4N', time: '2h ago', tag: 'Quote sent' },
    ]},
    { title: 'Booked', color: 'bg-emerald-50 border-emerald-200', dot: 'bg-emerald-400', leads: [
      { name: 'James Liu', pkg: 'Europe 10N • £3,200', time: 'today', tag: 'Confirmed' },
      { name: 'Fatima Al Sayed', pkg: 'Thailand 6N', time: 'today', tag: 'Paid' },
    ]},
  ];
  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">Lead Pipeline</p>
        <span className="text-xs text-teal-600 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5 font-medium">5 active leads</span>
      </div>
      <div className="flex-1 grid grid-cols-3 gap-2 overflow-hidden">
        {columns.map((col, ci) => (
          <div key={col.title} className={`${col.color} border rounded-lg p-2 flex flex-col gap-2 transition-all duration-300 ${highlight === ci ? 'ring-2 ring-offset-1 ring-teal-400 scale-[1.02]' : ''}`}>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${col.dot}`} />
              <p className="text-[10px] font-semibold text-gray-600">{col.title}</p>
              <span className="ml-auto text-[10px] text-gray-400">{col.leads.length}</span>
            </div>
            {col.leads.map((lead) => (
              <div key={lead.name} className="bg-white rounded-md p-2 border border-white/60">
                <p className="text-[10px] font-semibold text-gray-800">{lead.name}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{lead.pkg}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[9px] text-gray-400">{lead.time}</span>
                  <span className="text-[9px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{lead.tag}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[['ri-robot-line','AI sorted 100%','teal'],['ri-time-line','Avg. 1.2s reply','emerald'],['ri-arrow-up-line','↑ 34% this week','amber']].map(([icon, label, c]) => (
          <div key={label} className={`bg-${c}-50 border border-${c}-200 rounded-lg p-2 flex items-center gap-1.5`}>
            <div className={`w-5 h-5 flex items-center justify-center`}>
              <i className={`${icon} text-${c}-600 text-xs`} />
            </div>
            <p className={`text-[10px] text-${c}-700 font-medium`}>{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Scene 4 – Quote Builder ──────────────────────────── */
function Scene4() {
  const [progress, setProgress] = useState(0);
  const [lines, setLines] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setProgress(p => Math.min(p + 2, 100));
      setLines(l => Math.min(l + 0.3, 4));
    }, 80);
    return () => clearInterval(t);
  }, []);
  const items = [
    { icon: 'ri-flight-takeoff-line', label: 'Return Flights (2 pax)', price: '$680', done: lines >= 1 },
    { icon: 'ri-hotel-line', label: 'Ubud Boutique Resort 7N', price: '$840', done: lines >= 2 },
    { icon: 'ri-car-line', label: 'Airport Transfers x2', price: '$90', done: lines >= 3 },
    { icon: 'ri-ticket-line', label: 'Sunrise Trekking Tour', price: '$120', done: lines >= 4 },
  ];
  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-700">Building Quote — Sarah, Bali 7N</p>
        <span className="text-xs text-teal-600 font-medium">{progress}%</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all duration-100" style={{width:`${progress}%`}} />
      </div>
      <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 p-3 flex flex-col gap-2">
        <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">Package Components</p>
        {items.map((item) => (
          <div key={item.label} className={`flex items-center gap-2 p-2 rounded-md border transition-all duration-500 ${item.done ? 'bg-white border-teal-100' : 'bg-gray-50 border-gray-100 opacity-40'}`}>
            <div className={`w-7 h-7 flex items-center justify-center rounded-md ${item.done ? 'bg-teal-50' : 'bg-gray-100'}`}>
              <i className={`${item.icon} text-xs ${item.done ? 'text-teal-600' : 'text-gray-400'}`} />
            </div>
            <p className="flex-1 text-xs text-gray-700">{item.label}</p>
            <p className={`text-xs font-semibold ${item.done ? 'text-gray-900' : 'text-gray-300'}`}>{item.price}</p>
            {item.done && <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />}
          </div>
        ))}
      </div>
      <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg p-3 flex items-center justify-between">
        <div>
          <p className="text-white/70 text-[10px]">Total Package Price</p>
          <p className="text-white font-bold text-lg">$1,730 <span className="text-white/70 text-xs font-normal">/ 2 adults</span></p>
        </div>
        <div className="flex items-center gap-1.5 bg-white/20 rounded-md px-3 py-1.5">
          <i className="ri-send-plane-fill text-white text-xs" />
          <span className="text-white text-xs font-medium">Send Quote</span>
        </div>
      </div>
    </div>
  );
}

/* ── Scene 5 – Booking confirmation ──────────────────── */
function Scene5() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const timers = [
      setTimeout(() => setStep(1), 800),
      setTimeout(() => setStep(2), 2200),
      setTimeout(() => setStep(3), 3800),
      setTimeout(() => setStep(4), 5200),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);
  const checklistItems = [
    'Quote accepted by client',
    'Deposit invoice sent',
    'Supplier booking confirmed',
    'Travel voucher generated',
  ];
  return (
    <div className="flex flex-col h-full px-3 py-2 gap-3">
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 flex flex-col items-center gap-3 flex-1">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 ${step >= 4 ? 'bg-emerald-500 scale-110' : 'bg-emerald-100'}`}>
          <i className={`ri-checkbox-circle-fill text-3xl transition-colors duration-500 ${step >= 4 ? 'text-white' : 'text-emerald-300'}`} />
        </div>
        <div className="text-center">
          <p className="font-bold text-gray-800 text-sm">Bali Package Booked!</p>
          <p className="text-xs text-gray-500 mt-0.5">Sarah Ahmed • Sep 12–19, 2025</p>
        </div>
        <div className="w-full flex flex-col gap-2">
          {checklistItems.map((item, i) => (
            <div key={item} className={`flex items-center gap-2 p-2 rounded-lg border transition-all duration-500 ${step > i ? 'bg-white border-emerald-100' : 'bg-transparent border-transparent opacity-30'}`}>
              <div className={`w-5 h-5 flex items-center justify-center rounded-full transition-colors ${step > i ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                <i className="ri-check-line text-white text-xs" />
              </div>
              <p className="text-xs text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <p className="text-[10px] text-gray-400">Revenue</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5">$1,730</p>
          <p className="text-[10px] text-emerald-600">+$520 margin</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-2.5">
          <p className="text-[10px] text-gray-400">Time taken</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5">4 mins</p>
          <p className="text-[10px] text-teal-600">End-to-end</p>
        </div>
      </div>
    </div>
  );
}

/* ── Scene 6 – Autopilot dashboard ──────────────────────── */
function Scene6() {
  const [animate, setAnimate] = useState(false);
  useEffect(() => { setTimeout(() => setAnimate(true), 300); }, []);
  const bars = [40, 55, 70, 65, 80, 95, 88];
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2">
      <div className="grid grid-cols-2 gap-2">
        {[
          { label: 'Monthly Revenue', value: 'AED 184k', change: '+34%', icon: 'ri-money-dollar-circle-line', color: 'teal' },
          { label: 'Bookings', value: '47', change: '+21%', icon: 'ri-luggage-cart-line', color: 'emerald' },
          { label: 'Leads Handled by AI', value: '312', change: '100% auto', icon: 'ri-robot-line', color: 'amber' },
          { label: 'Avg. Response', value: '1.4s', change: '24/7 live', icon: 'ri-flashlight-line', color: 'rose' },
        ].map((kpi) => (
          <div key={kpi.label} className={`bg-${kpi.color}-50 border border-${kpi.color}-200 rounded-lg p-3`}>
            <div className={`w-7 h-7 flex items-center justify-center mb-1.5`}>
              <i className={`${kpi.icon} text-${kpi.color}-600 text-base`} />
            </div>
            <p className="text-[10px] text-gray-500">{kpi.label}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{kpi.value}</p>
            <p className={`text-[10px] text-${kpi.color}-600 font-medium`}>{kpi.change}</p>
          </div>
        ))}
      </div>
      <div className="flex-1 bg-white border border-gray-200 rounded-lg p-3">
        <p className="text-[10px] text-gray-500 font-medium mb-2">Bookings this week</p>
        <div className="flex items-end gap-1.5 h-16">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className="w-full rounded-t bg-gradient-to-t from-teal-500 to-emerald-400 transition-all duration-700"
                style={{ height: animate ? `${h}%` : '0%', transitionDelay: `${i * 80}ms` }}
              />
              <span className="text-[9px] text-gray-400">{days[i]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-3 flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
          <i className="ri-rocket-line text-white text-base" />
        </div>
        <p className="text-xs text-white">Your agency runs <strong className="text-teal-400">24/7 on autopilot</strong> — TreWaz handles the rest</p>
      </div>
    </div>
  );
}

const SceneComponents = [Scene1, Scene2, Scene3, Scene4, Scene5, Scene6];

export default function HeroVideoDemo() {
  const [currentScene, setCurrentScene] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startProgress = useCallback(() => {
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
    progressRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          if (progressRef.current) clearInterval(progressRef.current);
          return 100;
        }
        return p + (100 / (SCENE_DURATION / 100));
      });
    }, 100);
  }, []);

  const goToScene = useCallback((idx: number) => {
    setCurrentScene(idx);
    setProgress(0);
    startProgress();
  }, [startProgress]);

  useEffect(() => {
    if (!playing) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
      return;
    }
    startProgress();
    intervalRef.current = setInterval(() => {
      setCurrentScene(prev => {
        const next = (prev + 1) % scenes.length;
        return next;
      });
      startProgress();
    }, SCENE_DURATION);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [playing, startProgress]);

  const CurrentScene = SceneComponents[currentScene];

  return (
    <div className="relative w-full max-w-[480px] select-none">
      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-br from-teal-400/20 to-emerald-400/10 rounded-3xl blur-2xl -z-10" />

      {/* Main frame */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-xl">
        {/* Video-player title bar */}
        <div className="bg-gray-900 px-4 py-2.5 flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </div>
          <div className="flex-1 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-play-circle-fill text-teal-400 text-sm" />
            </div>
            <p className="text-xs text-white/80 font-medium">TreWaz — How It Works</p>
          </div>
          <span className="text-[10px] text-white/40">{currentScene + 1}/{scenes.length}</span>
        </div>

        {/* Scene content */}
        <div className="h-[380px] overflow-hidden relative" key={currentScene}>
          <div style={{animation:'fadeSlideIn 0.5s ease'}}>
            <CurrentScene />
          </div>
        </div>

        {/* Timeline bar */}
        <div className="bg-gray-900 px-4 py-3 flex flex-col gap-2">
          {/* Chapter dots */}
          <div className="flex items-center gap-1.5">
            {scenes.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goToScene(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${i === currentScene ? 'flex-1 h-1.5 bg-teal-400' : 'w-3 h-1.5 bg-white/20 hover:bg-white/40'}`}
              />
            ))}
          </div>
          {/* Controls row */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPlaying(p => !p)}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className={`${playing ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-sm`} />
            </button>
            <button onClick={() => goToScene((currentScene - 1 + scenes.length) % scenes.length)} className="w-6 h-6 flex items-center justify-center cursor-pointer">
              <i className="ri-skip-back-mini-fill text-white/60 hover:text-white text-sm transition-colors" />
            </button>
            <button onClick={() => goToScene((currentScene + 1) % scenes.length)} className="w-6 h-6 flex items-center justify-center cursor-pointer">
              <i className="ri-skip-forward-mini-fill text-white/60 hover:text-white text-sm transition-colors" />
            </button>
            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-teal-400 rounded-full transition-none" style={{width:`${progress}%`}} />
            </div>
            <span className="text-[10px] text-white/40 w-8 text-right">
              {Math.floor(((currentScene * SCENE_DURATION) + (progress / 100 * SCENE_DURATION)) / 1000)}s
            </span>
          </div>
          {/* Chapter label */}
          <p className="text-[10px] text-white/40 truncate">
            Chapter {currentScene + 1}: <span className="text-white/60">{scenes[currentScene].chapter}</span> — {scenes[currentScene].title}
          </p>
        </div>
      </div>

      {/* Floating label top-right */}
      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full px-3 py-1.5 shadow-lg">
        <p className="text-white text-[10px] font-semibold whitespace-nowrap">60s Explainer</p>
      </div>

      {/* Floating bottom-left */}
      <div className="absolute -bottom-3 -left-3 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-md flex items-center gap-2">
        <div className="w-6 h-6 flex items-center justify-center">
          <i className="ri-robot-2-line text-teal-500 text-base" />
        </div>
        <div>
          <p className="text-[10px] font-semibold text-gray-800">AI-Powered Demo</p>
          <p className="text-[10px] text-gray-400">Live • No signup needed</p>
        </div>
      </div>
    </div>
  );
}
